import { createRouter as createTanStackRouter } from "@tanstack/solid-router";
import { NotFound } from "./components/not-found";
import { getContext } from "./integrations/tanstack-query/provider";
import { routeTree } from "./routeTree.gen";

// DEBUG: remove once ownership warning is resolved
// if (typeof window !== "undefined" && import.meta.env.DEV) {
//   import("./debug-solid").then(({ installSolidOwnerDebug }) =>
//     installSolidOwnerDebug(),
//   );
// }

let _sentryInitialized = false;
let _gtagInitialized = false;

export function getRouter() {
  const router = createTanStackRouter({
    routeTree,

    context: getContext(),

    scrollRestoration: true,
    // Preload route loaders on link hover/focus ("intent") so client-side
    // navigations resolve from cache instead of blocking on the loader — this
    // removes the "flash" when navigating between routes (e.g. table → detail).
    // This matches the intent noted in __root.tsx; Cloudflare's blanket
    // speculation rules are opted out there, so TanStack's own preload is safe.
    defaultPreload: "intent",
    // CRITICAL: without a staleTime, a preloaded loader result is immediately
    // considered stale on navigation (staleTime defaults to 0), so the loader
    // re-runs — causing a double DB call (preload + navigation). Setting a
    // default staleTime makes preloaded data fresh for 30s, so navigation
    // reuses the preload result instead of re-fetching.
    defaultStaleTime: 30_000,

    // Router-wide fallback for any not-found error not handled by a specific
    // route's notFoundComponent. Without this, TanStack Router renders a bare
    // `<p>Not Found</p>` which looked like the app had hung when an AI agent
    // linked to a route that doesn't exist.
    defaultNotFoundComponent: () => <NotFound />,
  });

  // Lazy-load the entire Sentry SDK (including @sentry-internal/replay ~40 KiB)
  // via requestIdleCallback so it only runs when the browser has spare cycles —
  // keeping it fully out of the critical path and reducing TBT.
  if (
    !_sentryInitialized &&
    typeof window !== "undefined" &&
    process.env.NODE_ENV !== "development"
  ) {
    _sentryInitialized = true;
    const load = () =>
      import("./integrations/sentry-browser").then(({ init }) => init(router));
    if ("requestIdleCallback" in window) {
      requestIdleCallback(load, { timeout: 4000 });
    } else {
      setTimeout(load, 1000);
    }
  }

  // Lazy-load Google Analytics (gtag.js) the same way — inject the script tag
  // only when the browser is idle so it never blocks first paint. The module
  // is a no-op when VITE_GA_MEASUREMENT_ID isn't set.
  if (
    !_gtagInitialized &&
    typeof window !== "undefined" &&
    process.env.NODE_ENV !== "development"
  ) {
    _gtagInitialized = true;
    const load = () => import("./integrations/gtag").then(({ init }) => init());
    if ("requestIdleCallback" in window) {
      requestIdleCallback(load, { timeout: 4000 });
    } else {
      setTimeout(load, 1000);
    }
  }

  return router;
}

declare module "@tanstack/solid-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
